/**
 * Accessibility helper: detect when the user has scaled up text either via
 *   - device-level font size settings (iOS Dynamic Type, Android display
 *     font scale)
 *   - browser zoom / pinch zoom
 *   - admin-only `?textScale=` URL override (for QA without changing
 *     device settings)
 *
 * When the effective scale crosses a threshold we add body classes:
 *   - text-scale-large  (>= 1.15x)  — show overflow scrollbars on critical
 *                                     containers (modals, headers).
 *   - text-scale-xl     (>= 1.50x)  — additionally relax fixed-height
 *                                     cards and form rows so nothing is
 *                                     clipped.
 *
 * The corresponding CSS lives in index.css under the `text-scale-*`
 * selectors. Scrollbars are auto-hide / overlay style so they only appear
 * when content actually overflows.
 */
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

const LARGE_THRESHOLD = 1.15;
const XL_THRESHOLD = 1.5;
const BASE_FONT_PX = 16;

let currentScale = 1;
let adminOverride = null;
let started = false;

function readDocFontScale() {
  try {
    const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
    if (!isFinite(px) || px <= 0) return 1;
    return px / BASE_FONT_PX;
  } catch {
    return 1;
  }
}

function readBrowserZoom() {
  try {
    // Visual viewport scale catches pinch-zoom; outerWidth / innerWidth
    // catches browser-level zoom (desktop Chrome/Firefox/Safari).
    let zoom = 1;
    if (window.visualViewport && window.visualViewport.scale > 1) {
      zoom = window.visualViewport.scale;
    } else if (window.outerWidth && window.innerWidth) {
      const ratio = window.outerWidth / window.innerWidth;
      // Mobile WebViews report outerWidth === 0 or near 1 ratio noise.
      if (ratio > 1.02 && ratio < 5) zoom = ratio;
    }
    return zoom;
  } catch {
    return 1;
  }
}

function effectiveScale() {
  if (adminOverride && adminOverride > 0) return adminOverride;
  const fontScale = readDocFontScale();
  const zoom = readBrowserZoom();
  // Combine but cap at 3x to avoid runaway values from buggy environments.
  return Math.min(Math.max(fontScale, zoom), 3);
}

function applyClasses(scale) {
  const body = document.body;
  if (!body) return;
  const large = scale >= LARGE_THRESHOLD;
  const xl = scale >= XL_THRESHOLD;
  body.classList.toggle('text-scale-large', large);
  body.classList.toggle('text-scale-xl', xl);
  document.documentElement.style.setProperty('--text-scale', scale.toFixed(2));
}

function recompute() {
  const next = effectiveScale();
  if (Math.abs(next - currentScale) < 0.01) return;
  currentScale = next;
  applyClasses(next);
}

async function maybeApplyAdminOverride() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('textScale');
    if (!raw) return;
    const val = parseFloat(raw);
    if (!isFinite(val) || val < 0.5 || val > 3) return;
    // Verify admin session before honoring the override.
    try {
      const res = await axios.get(`${API}/admin/auth/me`, { withCredentials: true });
      if (res?.data?.admin_id || res?.data?.user_id || res?.data?.email) {
        adminOverride = val;
        recompute();
        // eslint-disable-next-line no-console
        console.info(`[a11y] Admin textScale override active: ${val}x`);
      }
    } catch {
      // Not an admin — silently ignore the param.
    }
  } catch {
    /* ignore */
  }
}

export function initTextScaleObserver() {
  if (started) return;
  started = true;

  // First sync pass before override resolves so we don't flash.
  recompute();

  // Recompute on viewport / zoom / orientation events.
  const onResize = () => recompute();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onResize, { passive: true });
    window.visualViewport.addEventListener('scroll', onResize, { passive: true });
  }
  // Catch device-level Dynamic Type / Font Size changes when the app
  // returns to the foreground.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recompute();
  });

  // Admin override (async, non-blocking).
  maybeApplyAdminOverride();
}

export function getCurrentTextScale() {
  return currentScale;
}
